import { Controller } from 'react-hook-form';
import type { Control, UseFormClearErrors } from 'react-hook-form';

import { Stack } from 'ui/stack';
import { Input } from 'ui/input';
import { Select } from 'ui/select';
import { TextArea } from 'ui/text-area';

export interface ReportFormValues {
  title: string;
  description: string;
  powerBiId: string;
  productId: string;
}

export interface PowerBiReport {
  id: string;
  name?: string;
}

export interface ProductChoice {
  id: string | number;
  name?: string;
}

export interface PowerBiData {
  value?: string;
  label?: string;
}

interface ReportFormProps {
  control: Control<ReportFormValues>;
  disabled: boolean;
  powerBiReports: PowerBiReport[];
  clearErrors: UseFormClearErrors<ReportFormValues>;
  products: ProductChoice[];
  powerBiData: PowerBiData;
}

const ReportForm = ({
  control,
  disabled,
  powerBiReports,
  clearErrors,
  products,
  powerBiData,
}: ReportFormProps): React.JSX.Element => (
  <Stack direction="row" sx={{ gap: 4, height: '100%' }}>
    <Stack sx={{ gap: 1.5, width: '100%' }}>
      <Controller
        name="title"
        control={control}
        rules={{ required: 'This field is required' }}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            label="Report name"
            placeholder="Report name..."
            disabled={disabled}
            error={!!error}
            errorMessage={error?.message}
            required={true}
            sx={{ mt: 1.5 }}
            data-testid="report-name-input"
          />
        )}
      />

      <Controller
        name="description"
        control={control}
        rules={{ required: 'This field is required' }}
        render={({ field, fieldState: { error } }) => (
          <TextArea
            {...field}
            label="Description"
            placeholder="Please type report description..."
            disabled={disabled}
            rows={5}
            error={!!error}
            errorMessage={error?.message}
            required
            data-testid="report-description-textarea"
          />
        )}
      />
    </Stack>

    <Stack sx={{ gap: 1.5, width: '100%' }}>
      <Controller
        name="powerBiId"
        control={control}
        rules={{ required: 'This field is required' }}
        render={({ field, fieldState: { error } }) => {
          const transformedOptions = powerBiReports.map((report) => ({
            value: report.id,
            label: report.name ?? '',
          }));

          if (powerBiData.value) {
            transformedOptions.unshift({
              value: powerBiData.value,
              label: powerBiData.label ?? '',
            });
          }

          return (
            <Select
              {...field}
              label="PowerBI title"
              disabled={disabled}
              options={transformedOptions}
              error={!!error}
              errorMessage={error && 'This field is required'}
              required
              sx={{ mt: 1.5 }}
              onChange={(selectedOption) => {
                field.onChange(
                  (selectedOption as { value: string } | null)?.value
                );
              }}
              value={field.value}
              clearInputErr={() => clearErrors('powerBiId')}
              data-testid="report-powerbi-title-select"
            />
          );
        }}
      />

      <Controller
        name="productId"
        control={control}
        render={({ field }) => {
          const transformedOptions = products.map((product) => ({
            value: String(product.id),
            label: product.name ?? '',
          }));

          return (
            <Select
              {...field}
              label="Associated product"
              disabled={disabled}
              options={transformedOptions}
              onChange={(selectedOption) => {
                field.onChange(
                  (selectedOption as { value: string } | null)?.value || ''
                );
              }}
              value={field?.value || ''}
              clearable
              sx={{ mt: 1.5 }}
              data-testid="report-associated-product-select"
            />
          );
        }}
      />
    </Stack>
  </Stack>
);

export default ReportForm;
